import PropTypes from "prop-types";
import { useState } from "react";
import { QuestionCircleOutlined } from "@ant-design/icons";
import {
  BbotAccordion,
  Button,
  Checkbox,
  Collapse,
  Input,
  notification,
  Select,
  SettingsCard,
  Tooltip,
} from "top-component-library";
import { buildMenuPayload, initialOrdersSchedule } from "../../menuUtils";
import { SaveRow, SettingControl } from "../../styles";
import AdvancedOrderAheadSection from "./AdvancedOrderAheadSection";
import OrderTimingSection from "./OrderTimingSection";
import UnavailableItemsSection from "./UnavailableItemsSection";

// One menu's settings: name + "displayed as" merge + accepts-future-orders, then
// three collapsible sections. Editing is local; Save validates and normalizes via
// buildMenuPayload (a toast on any error, matching the legacy page) before saving.
const MenuSettingsCard = ({ menu: initialMenu, allMenus, forceAllowOrderAhead, onSave }) => {
  const [menu, setMenu] = useState(initialMenu);
  const [ordersSchedule, setOrdersSchedule] = useState(() => initialOrdersSchedule(initialMenu));

  const updateField = (field, value) => setMenu((current) => ({ ...current, [field]: value }));

  const updateScheduleDay = (scheduleField, day, value) =>
    setMenu((current) => ({
      ...current,
      [scheduleField]: {
        ...current[scheduleField],
        [day]: { ...current[scheduleField][day], customer_input: value },
      },
    }));

  const updateDateRanges = (scheduleType, whitelist, blacklist) =>
    setMenu((current) => ({
      ...current,
      [`${scheduleType}_date_ranges_whitelist`]: whitelist,
      [`${scheduleType}_date_ranges_blacklist`]: blacklist,
    }));

  const handleSave = () => {
    const result = buildMenuPayload(menu, ordersSchedule);
    if (result.error) {
      notification.error({ message: result.error });
      return;
    }
    onSave(result.payload);
  };

  const otherMenus = allMenus.filter((other) => other.menuId !== menu.menuId);
  const menuLabel = menu.name_for_owner || menu.menuName;

  return (
    <SettingsCard title={`Menu: ${menuLabel}`}>
      <div>
        <SettingControl>
          <label htmlFor={`${menu.menuId}-name`}>Menu name</label>
          <Input
            id={`${menu.menuId}-name`}
            value={menu.menuName ?? ""}
            onChange={(event) => updateField("menuName", event.target.value)}
          />
        </SettingControl>
        {otherMenus.length > 0 && (
          <SettingControl className="margin-top-2">
            <label htmlFor={`${menu.menuId}-merge`}>Displayed as</label>
            <Select
              id={`${menu.menuId}-merge`}
              style={{ width: 320 }}
              value={menu.combine_into_id || ""}
              onChange={(value) => updateField("combine_into_id", value || null)}
            >
              <Select.Option value="">Its own tab</Select.Option>
              {otherMenus.map((other) => (
                <Select.Option key={other.menuId} value={other.menuId}>
                  Part of {other.name_for_owner}
                </Select.Option>
              ))}
            </Select>
          </SettingControl>
        )}
        <div className="margin-top-2">
          <Checkbox
            checked={Boolean(menu.allow_order_ahead)}
            disabled={forceAllowOrderAhead}
            onChange={(event) => updateField("allow_order_ahead", event.target.checked)}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span>This menu accepts future orders</span>
              <Tooltip title="Guests can schedule orders up to a week in advance. Scheduled orders show on the 'Future Orders' page of the prep station.">
                <QuestionCircleOutlined style={{ padding: 4 }} />
              </Tooltip>
            </span>
          </Checkbox>
        </div>
      </div>
      <div>
        <BbotAccordion>
          <Collapse.Panel header="Edit when guests can place orders" key="timing">
            <OrderTimingSection
              menu={menu}
              allMenus={allMenus}
              ordersSchedule={ordersSchedule}
              onOrdersScheduleChange={setOrdersSchedule}
              onScheduleDayChange={updateScheduleDay}
              onDateRangesChange={updateDateRanges}
            />
          </Collapse.Panel>
          <Collapse.Panel header="Edit how unavailable items are shown" key="unavailable">
            <UnavailableItemsSection menu={menu} onChange={updateField} />
          </Collapse.Panel>
          <Collapse.Panel header="Edit advanced order-ahead features" key="advanced">
            <AdvancedOrderAheadSection menu={menu} onChange={updateField} />
          </Collapse.Panel>
        </BbotAccordion>
        <SaveRow>
          <Button type="primary" onClick={handleSave}>
            Save {menuLabel} Settings
          </Button>
        </SaveRow>
      </div>
    </SettingsCard>
  );
};

MenuSettingsCard.propTypes = {
  menu: PropTypes.object.isRequired,
  allMenus: PropTypes.arrayOf(PropTypes.object).isRequired,
  forceAllowOrderAhead: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
};

export default MenuSettingsCard;
